import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Phone, Search, UserPlus, MapPin, Star, ArrowRight, X as XIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Caller-ID lookup dialog. Opens from:
 *   • sidebar «📞 Νέα κλήση» button
 *   • delivery top-right button
 *   • auto-prompt on /delivery (first visit per session)
 *
 * Flow:
 *   1. operator types the inbound caller's 10-digit phone
 *   2. POST /api/customers/lookup-by-phone
 *   3. if found → show customer + address picker → onSelect(cust, addr) bubbles up
 *      if not found → «Νέος πελάτης» button that pre-fills the phone in the customer dialog
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   onSelect?: ({customer, address}) => void  — closing callback for delivery wiring
 */
export default function CallerLookupDialog({ open, onOpenChange, onSelect }) {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {found, customer}
  const [selectedAddr, setSelectedAddr] = useState(null);

  useEffect(() => {
    if (open) { setPhone(""); setResult(null); setSelectedAddr(null); }
  }, [open]);

  const onPhoneChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(v);
    if (v.length < 4) setResult(null);
  };

  const lookup = async () => {
    if (phone.length !== 10) {
      toast.error("Πληκτρολογήστε 10 ψηφία");
      return;
    }
    setBusy(true);
    try {
      const r = await http.get(`/customers/lookup-by-phone?phone=${phone}`);
      setResult(r.data);
      if (r.data.found) {
        const def = (r.data.customer.addresses || []).find((a) => a.is_default && a.status === "active")
          || (r.data.customer.addresses || []).find((a) => a.status === "active");
        setSelectedAddr(def?.id || null);
      }
    } catch (e) {
      toast.error("Σφάλμα αναζήτησης");
    } finally { setBusy(false); }
  };

  const proceed = (target) => {
    if (!result?.customer) return;
    const addr = (result.customer.addresses || []).find((a) => a.id === selectedAddr) || null;
    onSelect?.({ customer: result.customer, address: addr });
    onOpenChange(false);
    if (target) navigate(target);
  };

  const createNew = () => {
    // Hand off to Customers page with the phone pre-filled.
    onOpenChange(false);
    navigate(`/customers?phone=${phone}&new=1`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="caller-lookup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone size={20} className="text-blue-600" /> Νέα Κλήση</DialogTitle>
          <DialogDescription>Πληκτρολογήστε τον αριθμό από την αναγνώριση κλήσης.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              autoFocus
              value={phone}
              onChange={onPhoneChange}
              onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
              placeholder="10 ψηφία"
              maxLength={10}
              className="font-mono text-lg flex-1"
              data-testid="caller-lookup-phone"
            />
            <Button onClick={lookup} disabled={busy || phone.length !== 10} data-testid="caller-lookup-search">
              <Search size={14} className="mr-1" /> Αναζήτηση
            </Button>
          </div>

          {result && result.found && (
            <Card className="p-3 bg-emerald-50 border-emerald-300 space-y-2" data-testid="caller-lookup-found">
              <div className="flex items-center justify-between">
                <div className="font-bold text-emerald-900">{result.customer.name}</div>
                <div className="text-xs text-emerald-700">
                  {(result.customer.orders_count || 0)} παραγγελίες · {(result.customer.total_spent || 0).toFixed(2)}€
                </div>
              </div>
              <div className="text-xs text-slate-600 font-mono">{(result.customer.phones || []).join(" · ")}</div>
              <Label className="text-xs font-bold flex items-center gap-1"><MapPin size={12} /> Επιλέξτε διεύθυνση</Label>
              <div className="space-y-1.5">
                {(result.customer.addresses || []).filter((a) => a.status !== "deleted").map((a) => (
                  <label
                    key={a.id}
                    className={`flex items-start gap-2 p-2 rounded border cursor-pointer ${selectedAddr === a.id ? "bg-white border-emerald-500 ring-2 ring-emerald-200" : "bg-slate-50 border-slate-200"} ${a.status === "paused" ? "opacity-60" : ""}`}
                    data-testid={`caller-lookup-addr-${a.id}`}
                  >
                    <input type="radio" name="addr" checked={selectedAddr === a.id} onChange={() => setSelectedAddr(a.id)} className="mt-1" />
                    <div className="flex-1 text-xs">
                      <div className="font-bold flex items-center gap-1.5">
                        {a.area || "(χωρίς περιοχή)"}
                        {a.is_default && <Star size={11} className="text-yellow-500" />}
                        {a.status === "paused" && <span className="text-amber-600 text-[10px]">[ΠΑΥΣΗ]</span>}
                      </div>
                      <div className="text-slate-600">Ορ. {a.floor || "—"} · Κορδ. {a.doorbell || "—"}</div>
                      {a.instructions && <div className="text-slate-500 italic">{a.instructions}</div>}
                    </div>
                  </label>
                ))}
                {(result.customer.addresses || []).filter((a) => a.status !== "deleted").length === 0 && (
                  <p className="text-xs text-slate-400 italic">Δεν υπάρχει διεύθυνση. Προσθέστε από την καρτέλα.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button variant="outline" onClick={() => proceed("/delivery")} data-testid="caller-lookup-to-delivery">
                  <ArrowRight size={14} className="mr-1" /> Delivery
                </Button>
                <Button variant="outline" onClick={() => proceed("/pos")} data-testid="caller-lookup-to-retail">
                  <ArrowRight size={14} className="mr-1" /> Ταμείο
                </Button>
              </div>
            </Card>
          )}

          {result && !result.found && (
            <Card className="p-4 bg-amber-50 border-amber-300 space-y-2" data-testid="caller-lookup-notfound">
              <div className="text-sm text-amber-900">Δεν βρέθηκε πελάτης με αυτό το τηλέφωνο.</div>
              <Button onClick={createNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="caller-lookup-new-customer">
                <UserPlus size={14} className="mr-1" /> Νέος Πελάτης
              </Button>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="caller-lookup-cancel">
            <XIcon size={14} className="mr-1" /> Κλείσιμο
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
